import React, { useState } from 'react';
import './PromptEngineeringPage.css';

export function PromptEngineeringPage() {
  const [activeExample, setActiveExample] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [announceMessage, setAnnounceMessage] = useState('');

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setAnnounceMessage('Text copied to clipboard');
      setTimeout(() => {
        setCopiedIndex(null);
        setAnnounceMessage('');
      }, 2000);
    }).catch(() => {
      setAnnounceMessage('Failed to copy text');
    });
  };

  // Only core Good vs Poor examples (no personas)
  const examples = [
    {
      title: 'Basic Question',
      poor: 'Tell me about inventory',
      good: 'Explain the Economic Order Quantity (EOQ) model, including its assumptions, formula, and a practical example in retail inventory management.',
      explanation: 'Specific, requests assumptions, formula, and practical example.'
    },
    {
      title: 'Problem Solving',
      poor: 'Help me with my supply chain problem',
      good: 'I manage a warehouse with 10,000 SKUs. Our picking accuracy is 92%, and average pick time is 45 seconds. What specific strategies can improve both metrics, and how should I measure success?',
      explanation: 'Provides context, metrics, and asks for actionable strategies.'
    },
    {
      title: 'Analysis Request',
      poor: 'Analyze this data',
      good: 'Using the bullwhip effect framework, analyze how a 10% demand increase at retail level might amplify through a 4-tier supply chain. Include potential mitigation strategies.',
      explanation: 'Specifies framework, parameter, and mitigation strategies.'
    }
  ];

  return (
    <div className="prompt-page">
      <div className="container">
        {/* ARIA live region for copy announcements */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {announceMessage}
        </div>

        <header className="page-header">
          <h1>Prompt Engineering for Supply Chain AI</h1>
          <p>Craft effective prompts with Prof. Castillo's core rules, terminology, and techniques.</p>
        </header>

        {/* Core Components with Six Rules */}
        <section className="prompt-section">
          <h2>Prof. C's Six Rules for Effective Prompts</h2>
          <div className="components-grid">
            <div className="component-card">
              <h3>1. No Proprietary Data</h3>
              <p>Do not upload confidential or partner data unless using a secure enterprise/local deployment.</p>
            </div>
            <div className="component-card">
              <h3>2. Clarity & Detail</h3>
              <p>Write clear instructions: include context, requirements, output format, technical parameters, and constraints.</p>
            </div>
            <div className="component-card">
              <h3>3. Start Simple</h3>
              <p>Begin with basic prompts and gradually add complexity, validating each step.</p>
            </div>
            <div className="component-card">
              <h3>4. Reality Check</h3>
              <p>Verify AI outputs against known facts and watch for hallucinations or errors.</p>
            </div>
            <div className="component-card">
              <h3>5. Explainability</h3>
              <p>Ensure you understand and can articulate every part of the AI-generated solution before using it.</p>
            </div>
            <div className="component-card">
              <h3>6. Third Eye</h3>
              <p>Use GenAI as a squeegee for your third eye: it clarifies insights but relies on your expertise.</p>
            </div>
          </div>
        </section>

        {/* Key Terminology */}
        <section className="prompt-section">
          <h2>Key Terminology</h2>
          <ul>
            <li><strong>Model:</strong> AI engine (e.g., GPT-4o, Claude).</li>
            <li><strong>Token:</strong> Text chunk processed by the model (~¾ of a word).</li>
            <li><strong>System/User/Assistant:</strong> Role-based messages structuring the chat.</li>
            <li><strong>Context Window:</strong> Maximum tokens the model can process.</li>
            <li><strong>Temperature:</strong> Controls randomness.</li>
            <li><strong>Top-k/Top-p:</strong> Sampling parameters.</li>
          </ul>
        </section>

        {/* Advanced Prompting Techniques */}
        <section className="prompt-section">
          <h2>Advanced Prompting Techniques</h2>
          <div className="advanced-techniques">
            <div className="technique">
              <h3>Zero/Few/Many-Shot</h3>
              <p>Provide examples to show the AI the pattern you want it to follow. Zero-shot = no examples, Few-shot = 1-3 examples, Many-shot = several examples.</p>
              <div className="code-example-wrapper">
                <div className="code-example">
{`Classify these supply chain events as "Risk" or "Opportunity":

Examples:
- "Port strike announced" → Risk
- "New trade agreement signed" → Opportunity
- "Competitor's factory closure" → Opportunity

Now classify:
- "Raw material prices increased 15%"
- "New shipping route opened"`}
                </div>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard(`Classify these supply chain events as "Risk" or "Opportunity":

Examples:
- "Port strike announced" → Risk
- "New trade agreement signed" → Opportunity
- "Competitor's factory closure" → Opportunity

Now classify:
- "Raw material prices increased 15%"
- "New shipping route opened"`, 'few-shot')}
                  aria-label="Copy few-shot learning example to clipboard"
                >
                  {copiedIndex === 'few-shot' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="technique">
              <h3>Chain of Thought</h3>
              <p>Ask the AI to explain its reasoning step-by-step.</p>
              <div className="code-example-wrapper">
                <div className="code-example">
                  "Let's think through this step-by-step. First, calculate the safety stock needed for a 95% service level..."
                </div>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard("Let's think through this step-by-step. First, calculate the safety stock needed for a 95% service level...", 'cot')}
                  aria-label="Copy chain of thought example to clipboard"
                >
                  {copiedIndex === 'cot' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="technique">
              <h3>Role Playing</h3>
              <p>Have the AI assume a specific role or perspective.</p>
              <div className="code-example-wrapper">
                <div className="code-example">
                  "As a supply chain consultant, review my distribution network design and suggest improvements..."
                </div>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard("As a supply chain consultant, review my distribution network design and suggest improvements...", 'role')}
                  aria-label="Copy role playing example to clipboard"
                >
                  {copiedIndex === 'role' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="technique">
              <h3>Comparative Analysis</h3>
              <p>Request comparisons between different approaches.</p>
              <div className="code-example-wrapper">
                <div className="code-example">
                  "Compare JIT vs JIC inventory strategies for a semiconductor manufacturer during supply uncertainty..."
                </div>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard("Compare JIT vs JIC inventory strategies for a semiconductor manufacturer during supply uncertainty...", 'compare')}
                  aria-label="Copy comparative analysis example to clipboard"
                >
                  {copiedIndex === 'compare' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="technique">
              <h3>Constraint Definition</h3>
              <p>Specify limitations or requirements.</p>
              <div className="code-example-wrapper">
                <div className="code-example">
                  "Design a cross-docking operation for a 30,000 sq ft facility with a $500K budget..."
                </div>
                <button
                  className="copy-button"
                  onClick={() => copyToClipboard("Design a cross-docking operation for a 30,000 sq ft facility with a $500K budget...", 'constraint')}
                  aria-label="Copy constraint definition example to clipboard"
                >
                  {copiedIndex === 'constraint' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Iterative Refinement Workflow */}
        <section className="prompt-section">
          <h2>Iterative Refinement Workflow</h2>
          <div className="formula-card">
            <div className="formula">
              <span className="formula-part">Prompt</span>
              <span className="plus">→</span>
              <span className="formula-part">Review AI Output</span>
              <span className="plus">→</span>
              <span className="formula-part">Tweak & Re-prompt</span>
            </div>
          </div>
        </section>

        {/* Bias & Safety Guards */}
        <section className="prompt-section">
          <h2>Bias & Safety Guards</h2>
          <ul>
            <li>Implement guardrails to prevent harmful or nonsensical outputs.</li>
            <li>Monitor for biased or inappropriate content.</li>
            <li>Use filters and safe-completion settings when available.</li>
          </ul>
          <div className="safety-examples">
            <h4>Sample Guard Statements (copy and paste into your system prompt)</h4>
            <div className="code-example-wrapper">
              <div className="code-example">
                "Please avoid using any language that could be considered discriminatory or offensive."
              </div>
              <button
                className="copy-button"
                onClick={() => copyToClipboard("Please avoid using any language that could be considered discriminatory or offensive.", 'guard-1')}
                aria-label="Copy safety guard statement to clipboard"
              >
                {copiedIndex === 'guard-1' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <div className="code-example-wrapper">
              <div className="code-example">
                "If responses contain potential misinformation, flag and verify against trusted sources."
              </div>
              <button
                className="copy-button"
                onClick={() => copyToClipboard("If responses contain potential misinformation, flag and verify against trusted sources.", 'guard-2')}
                aria-label="Copy misinformation verification guard to clipboard"
              >
                {copiedIndex === 'guard-2' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <div className="code-example-wrapper">
              <div className="code-example">
                "Ensure all outputs comply with our ethical guidelines and omit references to protected attributes."
              </div>
              <button
                className="copy-button"
                onClick={() => copyToClipboard("Ensure all outputs comply with our ethical guidelines and omit references to protected attributes.", 'guard-3')}
                aria-label="Copy ethical guidelines guard to clipboard"
              >
                {copiedIndex === 'guard-3' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <div className="code-example-wrapper">
              <div className="code-example">
                "As a study aid for homework, use suggestions guide the user in discovering the answer for themselves. Remind the user to cite any AI-generated content appropriately."
              </div>
              <button
                className="copy-button"
                onClick={() => copyToClipboard("As a study aid for homework, use suggestions guide the user in discovering the answer for themselves. Remind the user to cite any AI-generated content appropriately.", 'guard-4')}
                aria-label="Copy homework study aid guard to clipboard"
              >
                {copiedIndex === 'guard-4' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
            <div className="code-example-wrapper">
              <div className="code-example">
                "As a study aid for exams, generate practice exam questions and detailed explanations, and ensure I verify all answers against my course materials."
              </div>
              <button
                className="copy-button"
                onClick={() => copyToClipboard("As a study aid for exams, generate practice exam questions and detailed explanations, and ensure I verify all answers against my course materials.", 'guard-5')}
                aria-label="Copy exam study aid guard to clipboard"
              >
                {copiedIndex === 'guard-5' ? '✓ Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </section>

        {/* Good vs. Poor Prompts */}
        <section className="prompt-section">
          <h2>Good vs. Poor Prompts</h2>
          <div className="examples-section">
            <div className="example-tabs" role="tablist" aria-label="Prompt examples">
              {examples.map((example, index) => (
                <button
                  key={index}
                  role="tab"
                  aria-selected={activeExample === index}
                  aria-controls={`tabpanel-${index}`}
                  id={`tab-${index}`}
                  className={`example-tab ${activeExample === index ? 'active' : ''}`}
                  onClick={() => setActiveExample(index)}
                  tabIndex={activeExample === index ? 0 : -1}
                >
                  {example.title}
                </button>
              ))}
            </div>
            <div
              className="example-content"
              role="tabpanel"
              id={`tabpanel-${activeExample}`}
              aria-labelledby={`tab-${activeExample}`}
            >
              <div className="prompt-comparison">
                <div className="poor-prompt">
                  <h4>❌ Poor Prompt</h4>
                  <p>"{examples[activeExample].poor}"</p>
                </div>
                <div className="good-prompt">
                  <h4>✅ Better Prompt</h4>
                  <p>"{examples[activeExample].good}"</p>
                  <button
                    className="copy-button-inline"
                    onClick={() => copyToClipboard(examples[activeExample].good, `example-${activeExample}`)}
                    aria-label={`Copy ${examples[activeExample].title} example to clipboard`}
                  >
                    {copiedIndex === `example-${activeExample}` ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="explanation">
                <strong>Why it's better:</strong> {examples[activeExample].explanation}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
